import React from 'react';
import { connect } from 'react-redux';

import {
  workflowAddReviewPhase,
  workflowRemoveReviewPhase,
  workflowSetReviewCoordinator,
  workflowSetReviewPhaseName,
  workflowSetStageConditionRule,
  workflowSortReviewPhases,
  workflowToggleSkipStage,
} from '~/actions/workflow';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { TicketStageType } from '~/enums/TicketStageType';
import { useIntl } from 'react-intl';
import { getReviewErrors, getReviewPhases } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { coerceName } from '~/utils/workflow';

import SkipStageAccordion from '../../shared/SkipStageAccordion';
import StageItems from '../../shared/StageItems';
import WorkflowUserRoleSingleSelect from '../../shared/WorkflowUserRoleSingleSelect';
import Phase from './Phase';

function Review({
  // connected
  conditionRule,
  coordinator,
  disabled,
  errors,
  isSkipped,
  reviewPhases,
  workflowAddReviewPhase,
  workflowSetReviewCoordinator,
  workflowSortReviewPhases,
  workflowRemoveReviewPhase,
  workflowSetReviewPhaseName,
  workflowSetStageConditionRule,
  workflowToggleSkipStage,
}) {
  const intl = useIntl();

  const reviewItems = reviewPhases.map((phase, index) => ({
    ...phase,
    title: coerceName(
      phase.title,
      intl.formatMessage({
        id: 'Review.defaultPhaseName',
        defaultMessage: 'Review Phase',
        description: 'Default name for a new review phase.',
      }),
      index,
    ),
    content: <Phase key={phase.id} phaseId={phase.id} position={index} />,
  }));

  const handleUpdateStageConditionRule = (updatedConditionRule) => {
    workflowSetStageConditionRule({
      conditionRule: updatedConditionRule,
      stage: TicketStageType.Review,
    });
  };

  const handleToggleSkipStage = (updatedIsSkipped) => {
    workflowToggleSkipStage({
      isSkipped: updatedIsSkipped,
      stage: TicketStageType.Review,
    });
  };

  return (
    <FlexLayout disabled={disabled} flexDirection="column" space={6}>
      <SkipStageAccordion
        conditionRule={conditionRule}
        isSkipped={isSkipped}
        onChange={handleUpdateStageConditionRule}
        onToggle={handleToggleSkipStage}
      />
      <FlexLayout flexDirection="column" mb={8} space={4}>
        <FormField
          description={intl.formatMessage({
            id: 'Review.coordinatorDescription',
            defaultMessage:
              'A review coordinator will be in charge of making sure that all the approvals have been collected.',
            description: 'Description for review coordinator field',
          })}
          id="workflow--review-coordinator"
          input={WorkflowUserRoleSingleSelect}
          label={intl.formatMessage({
            id: 'Review.coordinatorLabel',
            defaultMessage: 'Review Coordinator',
            description: 'Label for review coordinator field',
          })}
          placeholder={intl.formatMessage({
            id: 'Review.coordinatorPlaceholder',
            defaultMessage: 'Not specified',
            description: 'Placeholder for review coordinator field',
          })}
          width="fullWidth"
          value={coordinator}
          onChange={workflowSetReviewCoordinator}
          error={
            errors.unspecifiedCoordinator
              ? intl.formatMessage({
                  id: 'Review.coordinatorRequiredError',
                  defaultMessage: 'You need to specify a review coordinator.',
                  description:
                    'Error message when review coordinator is required',
                })
              : undefined
          }
        />
      </FlexLayout>
      <StageItems
        disabled={disabled}
        items={reviewItems}
        invalidPhaseIds={errors.invalidPhaseIds}
        removeModalTitle={intl.formatMessage({
          id: 'Review.removePhaseModalTitle',
          defaultMessage: 'Remove Review Phase',
          description: 'Title for remove review phase modal',
        })}
        sortModalTitle={intl.formatMessage({
          id: 'Review.reorderPhasesModalTitle',
          defaultMessage: 'Reorder Review Phases',
          description: 'Title for reorder review phases modal',
        })}
        onRemove={workflowRemoveReviewPhase}
        onSort={workflowSortReviewPhases}
        onEditTitle={workflowSetReviewPhaseName}
        newItemAction={{
          id: 'workflow--add-review-phase',
          text: intl.formatMessage({
            id: 'Review.addReviewPhaseButton',
            defaultMessage: 'Add review phase',
            description: 'Button text for adding a review phase',
          }),
          handler: workflowAddReviewPhase,
        }}
      />
    </FlexLayout>
  );
}

function mapStateToProps({ builder, workflow }) {
  const stage = workflow.stages[TicketStageType.Review];
  return {
    conditionRule: stage.conditionRule,
    coordinator: stage.coordinators[0] || null,
    disabled: !builder.shouldEnableAllComponents,
    reviewPhases: getReviewPhases(workflow),
    errors: getReviewErrors(workflow),
    isSkipped: !stage.isEnabled,
  };
}

export default connect(mapStateToProps, {
  workflowAddReviewPhase,
  workflowSetReviewCoordinator,
  workflowSortReviewPhases,
  workflowRemoveReviewPhase,
  workflowSetReviewPhaseName,
  workflowSetStageConditionRule,
  workflowToggleSkipStage,
})(Review);
